import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { registerInput, type RegisterInput } from '@gql/shared-types';
import { api, ApiError } from '../api/client.js';
import { TurnstileWidget } from '../components/TurnstileWidget.js';

type FormInput = Omit<RegisterInput, 'turnstileToken' | 'website' | 'einwilligung'> & {
  einwilligung: boolean;
};

export function Register() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [turnstileToken, setTurnstileToken] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const honeypotRef = useState('')[0]; // immer leer; nur Bots füllen aus

  const { register, handleSubmit, formState: { errors } } = useForm<FormInput>({
    mode: 'onBlur',
  });

  const humanReadableError = (code: string): string => {
    switch (code) {
      case 'captcha_failed': return t('register.errCaptcha');
      case 'validation_error': return t('register.errValidation');
      case 'rate_limit_exceeded': return t('register.errRateLimit');
      default: return t('register.errGeneric');
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    setServerError(null);
    if (!turnstileToken) {
      setServerError(t('register.captchaPlease'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = registerInput.parse({ ...data, turnstileToken, website: honeypotRef });
      await api.register(payload);
      navigate('/verify-sent', { state: { email: payload.email } });
    } catch (err) {
      setServerError(err instanceof ApiError ? humanReadableError(err.code) : t('register.errUnexpected'));
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="card">
      <h2 className="card-title">{t('register.title')}</h2>
      <p className="card-subtitle">{t('register.subtitle')}</p>

      {serverError && <div className="alert alert-error">{serverError}</div>}

      <form onSubmit={onSubmit} noValidate>
        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="vorname">{t('register.vorname')}</label>
            <input id="vorname" className="field-input" autoComplete="given-name"
              {...register('vorname', { required: t('register.required') })} />
            {errors.vorname && <p className="field-error">{errors.vorname.message}</p>}
          </div>
          <div className="field">
            <label className="field-label" htmlFor="nachname">{t('register.nachname')}</label>
            <input id="nachname" className="field-input" autoComplete="family-name"
              {...register('nachname', { required: t('register.required') })} />
            {errors.nachname && <p className="field-error">{errors.nachname.message}</p>}
          </div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="geburtsdatum">{t('register.geburtsdatum')}</label>
          <input id="geburtsdatum" type="date" className="field-input" autoComplete="bday"
            {...register('geburtsdatum', { required: t('register.required') })} />
          {errors.geburtsdatum && <p className="field-error">{errors.geburtsdatum.message}</p>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="email">{t('register.email')}</label>
          <input id="email" type="email" className="field-input" autoComplete="email"
            {...register('email', { required: t('register.required') })} />
          {errors.email && <p className="field-error">{errors.email.message}</p>}
        </div>

        {/* Honeypot — für Bots sichtbar, für Menschen nicht (CSS off-screen) */}
        <div className="honeypot" aria-hidden="true">
          <label>Website (bitte leer lassen)</label>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </div>

        <div className="checkbox-field">
          <input id="einwilligung" type="checkbox"
            {...register('einwilligung', { required: t('register.einwilligungRequired') })} />
          <label htmlFor="einwilligung">{t('register.einwilligung')}</label>
        </div>
        {errors.einwilligung && <p className="field-error">{errors.einwilligung.message}</p>}

        <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />

        <button className="button" type="submit" disabled={submitting || !turnstileToken}>
          {submitting ? t('register.submitting') : t('register.submit')}
        </button>
      </form>

      <p className="muted-block">
        {t('register.haveAccount')} <Link to="/login" className="text-link">{t('register.login')}</Link>
      </p>
    </div>
  );
}
